import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Group, GroupApiResponse, GroupDto } from '../models/Group';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

export interface GroupCreate {
  name: string;
  description?: string;
  createdBy: string;
}

export interface GroupWithPermissionsCreate {
  name: string;
  description?: string;
  createdBy: string;
  permissions: {
    name: string;
    description: string;
  }[];
}

export interface GroupUpdate {
  name?: string;
  description?: string;
  profileId?: number;
  updatedBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class GroupsService {
  // Usa proxy em desenvolvimento e host do microserviço em produção
  private baseUrl = environment.microservices.profileGroup;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {
  }

  // ===== ENDPOINTS DISPONÍVEIS NO BACKEND =====
  // ✅ CreateGroup - Criar grupo básico
  // ✅ GetGroups - Listar grupos com paginação
  // ✅ GetGroups/{id} - Buscar grupo por ID
  // ✅ UpdateGroup/{id} - Atualizar grupo
  // ✅ DeleteGroup/{id} - Deletar grupo (soft delete)
  // ✅ CreateGroupWithPermissions - Criar grupo com permissões
  //
  // ⚠️ ENDPOINTS NÃO IMPLEMENTADOS NO BACKEND:
  // ❌ CheckGroupNameAvailability - Validar nome disponível
  // ❌ CanDeleteGroup - Verificar se pode deletar
  // ❌ GetGroupWithPermissions/{id} - Buscar com permissões
  // ❌ CountPermissionsByGroup - Contar permissões
  // ❌ ExportGroups - Exportar dados
  // ❌ CloneGroup - Clonar grupo
  // ❌ GetGroupsStatistics - Estatísticas
  // ❌ CreateMultipleGroups - Criar múltiplos
  // ❌ DeleteMultipleGroups - Deletar múltiplos

  // ===== CRUD de Grupos =====

  /**
   * Busca todos os grupos
   */
  getGroups(page: number = 1, pageSize: number = 10): Observable<GroupApiResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<GroupApiResponse>(`${this.baseUrl}/GetGroups`, { params }).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar grupos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os grupos e retorna apenas os dados mapeados
   */
  getAllGroups(): Observable<Group[]> {
    return this.getGroups(1, 1000).pipe(
      map(response => {
        // Validação para evitar erro de undefined
        if (!response || !response.data) {
          return [];
        }
        return response.data.map(dto => this.mapFromGroupDTO(dto));
      }),
      catchError(err => {
        console.error('❌ [GroupsService] Erro ao buscar todos os grupos:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca grupos por perfil
   */
  getGroupsByProfile(profileId: number, page: number = 1, pageSize: number = 10): Observable<GroupApiResponse> {
    const params = new HttpParams()
      .set('profileId', profileId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<GroupApiResponse>(`${this.baseUrl}/GetGroups`, { params }).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar grupos por perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os grupos por perfil e retorna apenas os dados mapeados
   */
  getAllGroupsByProfile(profileId: number): Observable<Group[]> {
    return this.getGroupsByProfile(profileId).pipe(
      map(response => {
        // Validação para evitar erro de undefined
        if (!response || !response.data) {
          return [];
        }
        return response.data.map(dto => this.mapFromGroupDTO(dto));
      }),
      catchError(err => {
        console.error(`❌ [GroupsService] Erro ao buscar grupos do perfil ${profileId}:`, err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca grupo por ID
   */
  getGroupById(id: number): Observable<Group> {
    return this.http.get<GroupDto>(`${this.baseUrl}/GetGroups/${id}`).pipe(
      map(dto => this.mapFromGroupDTO(dto)),
      catchError(err => {
        this.errorService.showError('Erro ao buscar grupo');
        return throwError(() => err);
      })
    );
  }

  /**
   * Cria um novo grupo
   */
  createGroup(groupData: GroupCreate): Observable<Group> {
    return this.http.post<GroupDto>(`${this.baseUrl}/CreateGroup`, groupData).pipe(
      map(dto => this.mapFromGroupDTO(dto)),
      catchError(err => {
        this.errorService.showError('Erro ao criar grupo');
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza um grupo existente
   */
  updateGroup(id: number, groupData: GroupUpdate): Observable<Group> {
    return this.http.put<GroupDto>(`${this.baseUrl}/UpdateGroup/${id}`, groupData).pipe(
      map(dto => this.mapFromGroupDTO(dto)),
      catchError(err => {
        this.errorService.showError('Erro ao atualizar grupo');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove um grupo (soft delete)
   */
  deleteGroup(id: number, deletedBy: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/DeleteGroup/${id}`, {
      body: { deletedBy }
    }).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao excluir grupo');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Validação =====

  /**
   * Verifica se o nome do grupo está disponível
   * ⚠️ ATENÇÃO: Este endpoint NÃO existe no backend!
   * Retornará 404 - Resource Not Found
   * @deprecated Endpoint não implementado no backend
   */
  checkGroupNameAvailability(name: string, excludeId?: number): Observable<{ available: boolean }> {
    let params = new HttpParams().set('name', name);
    if (excludeId) {
      params = params.set('excludeId', excludeId.toString());
    }
    
    return this.http.get<{ available: boolean }>(
      `${this.baseUrl}/CheckGroupNameAvailability`, 
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao verificar disponibilidade do nome do grupo');
        return throwError(() => err);
      })
    );
  }

  /**
   * Verifica se o grupo pode ser deletado (não tem permissões associadas)
   * ⚠️ ATENÇÃO: Este endpoint NÃO existe no backend!
   * Retornará 404 - Resource Not Found
   * @deprecated Endpoint não implementado no backend
   */
  canDeleteGroup(id: number): Observable<{ canDelete: boolean; reason?: string }> {
    return this.http.get<{ canDelete: boolean; reason?: string }>(
      `${this.baseUrl}/CanDeleteGroup/${id}`
    ).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao verificar se o grupo pode ser excluído');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Busca e Filtro =====

  /**
   * Busca grupos independentes (sem perfil específico)
   */
  getIndependentGroups(page: number = 1, pageSize: number = 10): Observable<GroupApiResponse> {
    const params = new HttpParams()
      .set('profileId', '0')
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<GroupApiResponse>(`${this.baseUrl}/GetGroups`, { params }).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar grupos independentes');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca grupos por nome (filtra localmente ou usa endpoint se disponível)
   */
  searchGroupsByName(searchTerm: string, page: number = 1, pageSize: number = 10): Observable<Group[]> {
    return this.getAllGroups().pipe(
      map(groups => groups.filter(group => 
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
      ))
    );
  }

  // ===== Mapeamento de Dados =====

  /**
   * Mapeia dados recebidos da API para o modelo Group
   */
  private mapFromGroupDTO(dto: GroupDto): Group {
    return {
      id: dto.Id,
      name: dto.Name,
      description: dto.Description,
      profileId: dto.ProfileId,
      createdAt: new Date(dto.CreatedAt),
      updatedAt: new Date(dto.UpdatedAt),
      createdBy: dto.CreatedBy,
      updatedBy: dto.UpdatedBy
    };
  }

  /**
   * Cria um grupo com permissões específicas
   */
  createGroupWithPermissions(groupData: GroupWithPermissionsCreate): Observable<Group> {
    return this.http.post<GroupDto>(`${this.baseUrl}/CreateGroupWithPermissions`, groupData).pipe(
      map(dto => this.mapFromGroupDTO(dto)),
      catchError(err => {
        console.error('Erro na requisição CreateGroupWithPermissions:', err);
        this.errorService.showError('Erro ao criar grupo com permissões');
        return throwError(() => err);
      })
    );
  }

  /**
   * Mapeia dados do modelo Group para envio à API
   */
  private mapToGroupDTO(payload: any): any {
    return {
      name: payload.name,
      description: payload.description || '',
      profileId: payload.profileId || 0,
      createdBy: payload.createdBy,
      updatedBy: payload.updatedBy
    };
  }

  // ===== Batch Operations =====

  /**
   * Cria múltiplos grupos de uma vez
   */
  createMultipleGroups(groups: GroupCreate[]): Observable<Group[]> {
    return this.http.post<GroupDto[]>(`${this.baseUrl}/CreateMultipleGroups`, groups).pipe(
      map(dtos => dtos.map(dto => this.mapFromGroupDTO(dto))),
      catchError(err => {
        this.errorService.showError('Erro ao criar múltiplos grupos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Deleta múltiplos grupos de uma vez
   */
  deleteMultipleGroups(groupIds: number[], deletedBy: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/DeleteMultipleGroups`, {
      body: { groupIds, deletedBy }
    }).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao excluir múltiplos grupos');
        return throwError(() => err);
      })
    );
  }

  // ===== Advanced Queries =====

  /**
   * Busca grupos com suas permissões
   */
  getGroupsWithPermissions(page: number = 1, pageSize: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('includePermissions', 'true')
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<any>(`${this.baseUrl}/GetGroups`, { params }).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar grupos com permissões');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca grupo por ID com permissões
   * ⚠️ ATENÇÃO: Este endpoint NÃO existe no backend!
   * Use getGroupById() para buscar apenas dados do grupo
   * @deprecated Endpoint não implementado no backend
   */
  getGroupWithPermissionsById(groupId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/GetGroupWithPermissions/${groupId}`).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar grupo com permissões');
        return throwError(() => err);
      })
    );
  }

  /**
   * Conta quantas permissões um grupo tem
   * ⚠️ ATENÇÃO: Este endpoint NÃO existe no backend!
   * @deprecated Endpoint não implementado no backend
   */
  countPermissionsByGroup(groupId: number): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.baseUrl}/CountPermissionsByGroup/${groupId}`).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao contar permissões do grupo');
        return throwError(() => err);
      })
    );
  }

  // ===== Export/Import =====

  /**
   * Exporta grupos para backup
   */
  exportGroups(format: 'json' | 'csv' = 'json', profileId?: number): Observable<Blob> {
    let params = new HttpParams().set('format', format);
    
    if (profileId) {
      params = params.set('profileId', profileId.toString());
    }
    
    return this.http.get(`${this.baseUrl}/ExportGroups`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao exportar grupos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Clona um grupo com todas as suas permissões
   * ⚠️ ATENÇÃO: Este endpoint NÃO existe no backend!
   * @deprecated Endpoint não implementado no backend
   */
  cloneGroup(groupId: number, newName: string, newDescription: string, createdBy: string): Observable<Group> {
    return this.http.post<GroupDto>(`${this.baseUrl}/CloneGroup`, {
      sourceGroupId: groupId,
      name: newName,
      description: newDescription,
      createdBy
    }).pipe(
      map(dto => this.mapFromGroupDTO(dto)),
      catchError(err => {
        this.errorService.showError('Erro ao clonar grupo');
        return throwError(() => err);
      })
    );
  }

  // ===== Statistics =====

  /**
   * Busca estatísticas de grupos
   */
  getGroupsStatistics(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/GetGroupsStatistics`).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar estatísticas de grupos');
        return throwError(() => err);
      })
    );
  }
}
